import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LandingPage  from './pages/LandingPage';
import AppLayout    from './components/AppLayout';
import CalendarView from './pages/CalendarView';
import BookingPage  from './pages/BookingPage';
import ClientsPage  from './pages/ClientsPage';
import ServicesPage from './pages/ServicesPage';
import StatsPage    from './pages/StatsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage    from './pages/UsersPage';
import PrivacyPage  from './pages/PrivacyPage';
import TermsPage    from './pages/TermsPage';
import GdprPage     from './pages/GdprPage';
import FaqPage      from './pages/FaqPage';
import DocsPage     from './pages/DocsPage';
import TrialPage from './pages/TrialPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { initialized, authenticated } = useAuth();
  if (!initialized) return <div className="app-loading"><div className="spinner" /></div>;
  if (!authenticated) return <Navigate to="/" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { initialized, authenticated, user } = useAuth();
  if (!initialized) return <div className="app-loading"><div className="spinner" /></div>;
  if (!authenticated) return <Navigate to="/" replace />;
  if (!user?.isAdmin) return <Navigate to="/app/calendar" replace />;
  return children;
};

const RoleRoute = ({ children, roles }) => {
  const { initialized, authenticated, user } = useAuth();
  if (!initialized) return <div className="app-loading"><div className="spinner" /></div>;
  if (!authenticated) return <Navigate to="/" replace />;
  const userRoles = user?.roles || [];
  const hasAccess = user?.isAdmin || roles.some(r => userRoles.includes(r));
  if (!hasAccess) return <Navigate to="/app/calendar" replace />;
  return children;
};

const AppRoutes = () => {
  const { initialized, authenticated } = useAuth();
  if (!initialized) return <div className="app-loading"><div className="spinner" /></div>;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={authenticated ? <Navigate to="/app/calendar" replace /> : <LandingPage />} />

      {/* Legal pages — public, εκτός /app ── */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms"   element={<TermsPage />} />
      <Route path="/gdpr"    element={<GdprPage />} />
      <Route path="/faq"     element={<FaqPage />} />
      <Route path="/docs"    element={<DocsPage />} />
      <Route path="/trial" element={<TrialPage />} />

      {/* Protected — AppLayout wrapper */}
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="calendar" replace />} />
        <Route path="calendar" element={<CalendarView />} />
        <Route path="bookings" element={
          <RoleRoute roles={['salon-bookings', 'salon-clients']}>
            <BookingPage />
          </RoleRoute>
        } />
        <Route path="clients" element={
          <RoleRoute roles={['salon-clients']}>
            <ClientsPage />
          </RoleRoute>
        } />
        <Route path="services" element={<AdminRoute><ServicesPage /></AdminRoute>} />
        <Route path="stats"    element={<AdminRoute><StatsPage /></AdminRoute>} />
        <Route path="settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
        <Route path="users"    element={<AdminRoute><UsersPage /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;